import {HOST} from '../constants';

type JobStatusTypes = 'pending' | 'running' | 'completed' | 'failed';

interface JobStatus<T> {
    status: JobStatusTypes;
    result?: T;
    error?: string;
}

const BASE_JOBS_URL = `${HOST}/editor/jobs`;

export const listenToJob = <T>(
    jobId: string,
    onStatusChange?: (status: JobStatusTypes) => void,
    maxReconnectAttempts = 20,
    timeout = 3000
): Promise<T> => {
    return new Promise((resolve, reject) => {
        let source: EventSource | null = null;
        let isComplete = false;
        let reconnectAttempts = 0;

        const connect = (): void => {
            if (isComplete) return;

            source = new EventSource(`${BASE_JOBS_URL}/${jobId}/stream`, {
                withCredentials: true,
            });

            source.onmessage = (event) => {
                reconnectAttempts = 0;
                const job: JobStatus<T> = JSON.parse(event.data);

                onStatusChange?.(job.status);

                if (job.status === 'completed' && job.result) {
                    isComplete = true;
                    resolve(job.result);
                    source?.close();
                } else if (job.status === 'failed') {
                    isComplete = true;
                    reject(new Error(job.error || 'Job failed'));
                    source?.close();
                }
            };

            source.onerror = () => {
                source?.close();

                if (isComplete) return;

                reconnectAttempts += 1;

                if (reconnectAttempts <= maxReconnectAttempts) {
                    setTimeout(connect, timeout);
                } else {
                    reject(new Error('SSE connection failed after maximum retry attempts'));
                }
            };
        };

        connect();
    });
};
